import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { GetItemPrices } from '../../libs/GraphQL';
import { filterActiveSchemas } from '../../utils/schemaLifecycle';
import { getActiveSchemaIdSet } from '../../utils/activeSchemaIds';
import buildTsv from '../../utils/buildTsv';
import copyToClipboard from '../../utils/copyToClipboard';
import TipsPanel from '../../components/atoms/TipsPanel';
import styles from './ItemPricesLanding.module.css';

const BASE_PRICES_TIPS = [
  {
    title: "Look at one fridge's prices",
    body: (
      <>
        Select a schema ID and click <strong>View Item Prices</strong>.
      </>
    ),
  },
  {
    title: "Compare one item's price across the fleet",
    body: (
      <>
        Click <strong>View Item Prices</strong> without picking a schema to load
        the whole fleet, then use the <strong>Item filter</strong> to type a SKU
        name — you&apos;ll see that item&apos;s price on every fridge that
        carries it.
      </>
    ),
  },
  {
    title: 'Round-trip edit',
    body: (
      <>
        Filter to what you want, click <strong>Copy to Clipboard</strong>, paste
        into Sheets or Excel, edit the prices, then paste those rows into the
        CSV on <strong>Update Base Prices</strong>. Column order matches — no
        reformatting needed.
      </>
    ),
  },
];

const INITIAL_RENDER_COUNT = 200;
const RENDER_BATCH_SIZE = 200;
const ITEM_FILTER_DEBOUNCE_MS = 500;

const ItemPricesLanding = () => {
  const [schemas, setSchemas] = useState([]);
  const [selectedSchema, setSelectedSchema] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [itemPrices, setItemPrices] = useState([]);
  const [showPrices, setShowPrices] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [itemFilter, setItemFilter] = useState('');
  const [itemFilterInput, setItemFilterInput] = useState('');
  const [filteredPrices, setFilteredPrices] = useState([]);
  const [allPriceData, setAllPriceData] = useState([]); // Cache all price data
  const [availableItems, setAvailableItems] = useState([]); // List of all unique items
  const [showItemDropdown, setShowItemDropdown] = useState(false);
  const [visibleCount, setVisibleCount] = useState(INITIAL_RENDER_COUNT);

  const sentinelRef = useRef(null);
  const itemFilterDebounceRef = useRef(null);

  useEffect(() => {
    const fetchPrices = async () => {
      setLoading(true);
      setError(null);
      try {
        // Kick off the active-id fetch up front so it runs alongside the
        // paginated price fetch below. Resolve to null on failure so a fleet
        // hiccup degrades to unfiltered prices rather than failing the page.
        const activeSchemaIdsPromise = getActiveSchemaIdSet().catch(() => null);

        // Fetch all item prices with pagination to get complete dataset
        let allItems = [];
        let nextToken = null;
        let pageCount = 0;
        const MAX_PAGES = 100; // Safety limit to prevent infinite loops

        do {
          if (++pageCount > MAX_PAGES) {
            console.warn(
              `Reached maximum page limit (${MAX_PAGES}) while fetching item prices`,
            );
            break;
          }
          // Only include nextToken in input if it exists (not null on first call)
          const input = nextToken ? { nextToken } : {};
          const response = await GetItemPrices(input);
          const items = response?.items || [];
          allItems = allItems.concat(items);
          nextToken = response?.nextToken || null;
        } while (nextToken);

        // Hide prices for decommissioned fridges; keep base templates.
        // If the active set is unavailable, show everything rather than nothing.
        const activeSchemaIds = await activeSchemaIdsPromise;
        const activeItems = activeSchemaIds
          ? filterActiveSchemas(allItems, activeSchemaIds, {
              includeBaseSchemas: true,
            })
          : allItems;

        setAllPriceData(activeItems); // Cache the full data
        // Extract unique schema IDs that have ITEM_PRICES pricing type
        // Note: GetItemPrices already filters to ITEM_PRICES schemas on the backend
        const uniqueSchemas = [
          ...new Set(activeItems.map((price) => price.schemaId)),
        ].sort();
        setSchemas(uniqueSchemas);

        // Extract unique item names for autocomplete
        const uniqueItems = [
          ...new Set(activeItems.map((price) => price.item).filter(Boolean)),
        ].sort();
        setAvailableItems(uniqueItems);
      } catch (err) {
        setError('Failed to fetch item prices.');
      } finally {
        setLoading(false);
      }
    };
    fetchPrices();
  }, []);

  const handleSchemaChange = (e) => {
    setSelectedSchema(e.target.value);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setShowDropdown(true);

    if (value === '' || value === 'All Schemas') {
      setSelectedSchema('ALL');
    } else {
      setSelectedSchema(value);
    }
  };

  const handleSchemaSelect = (schema) => {
    setSearchTerm(schema === 'ALL' ? 'All Schemas' : schema);
    setSelectedSchema(schema);
    setShowDropdown(false);
  };

  const handleInputFocus = () => {
    setShowDropdown(true);
  };

  const handleInputBlur = () => {
    // Delay hiding dropdown to allow for clicks
    setTimeout(() => setShowDropdown(false), 200);
  };

  // Item filter autocomplete handlers
  const clearItemFilterDebounce = () => {
    if (itemFilterDebounceRef.current) {
      clearTimeout(itemFilterDebounceRef.current);
      itemFilterDebounceRef.current = null;
    }
  };

  const handleItemFilterChange = (e) => {
    const value = e.target.value;
    setItemFilterInput(value);
    setShowItemDropdown(true);

    clearItemFilterDebounce();
    itemFilterDebounceRef.current = setTimeout(() => {
      setItemFilter(value);
    }, ITEM_FILTER_DEBOUNCE_MS);
  };

  const handleItemSelect = (item) => {
    clearItemFilterDebounce();
    setItemFilter(item);
    setItemFilterInput(item);
    setShowItemDropdown(false);
  };

  const handleItemFilterFocus = () => {
    setShowItemDropdown(true);
  };

  const handleItemFilterBlur = () => {
    // Delay hiding dropdown to allow for clicks
    setTimeout(() => setShowItemDropdown(false), 200);
  };

  // Filter schemas based on search term
  const filteredSchemas = schemas.filter((schema) =>
    schema.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // Filter available items based on current filter text
  // Memoized to avoid re-filtering on every render
  const { filteredItems, totalFilteredCount } = useMemo(() => {
    const filtered = availableItems.filter((item) =>
      item.toLowerCase().includes(itemFilterInput.toLowerCase()),
    );
    return {
      filteredItems: filtered.slice(0, 50),
      totalFilteredCount: filtered.length,
    };
  }, [availableItems, itemFilterInput]);
  useEffect(() => {
    return () => {
      clearItemFilterDebounce();
    };
  }, []);

  // Filter prices based on item filter
  useEffect(() => {
    if (itemFilter.trim() === '') {
      setFilteredPrices(itemPrices);
    } else {
      const filtered = itemPrices.filter((price) =>
        (price.item || '').toLowerCase().includes(itemFilter.toLowerCase()),
      );
      setFilteredPrices(filtered);
    }
  }, [itemFilter, itemPrices]);

  useEffect(() => {
    setVisibleCount(INITIAL_RENDER_COUNT);
  }, [filteredPrices]);

  const visiblePrices = useMemo(
    () => filteredPrices.slice(0, visibleCount),
    [filteredPrices, visibleCount],
  );

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const firstEntry = entries[0];
        if (firstEntry.isIntersecting) {
          setVisibleCount((prev) =>
            prev >= filteredPrices.length
              ? prev
              : Math.min(prev + RENDER_BATCH_SIZE, filteredPrices.length),
          );
        }
      },
      {
        threshold: 0.1,
      },
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [filteredPrices.length]);

  // Calculate how many schemas have the filtered item
  const getItemCoverage = () => {
    if (!itemFilter.trim() || !showPrices) return null;

    const itemName = itemFilter.trim();
    const schemasWithItem = new Set(
      allPriceData
        .filter(
          (price) =>
            (price.item || '').toLowerCase() === itemName.toLowerCase(),
        )
        .map((p) => p.schemaId),
    );

    // Determine target schemas based on selected schema
    let targetSchemas = schemas;
    if (selectedSchema !== 'ALL') {
      targetSchemas = [selectedSchema];
    }

    const foundCount = targetSchemas.filter((s) =>
      schemasWithItem.has(s),
    ).length;
    const totalCount = targetSchemas.length;
    const missingCount = totalCount - foundCount;

    return {
      itemName,
      foundCount,
      totalCount,
      missingCount,
      hasResults: foundCount > 0,
    };
  };

  const itemCoverage = getItemCoverage();

  const handleViewPrices = () => {
    setLoading(true);
    setError(null);
    try {
      // Use cached data instead of fetching again
      let prices = allPriceData;
      if (selectedSchema !== 'ALL') {
        prices = allPriceData.filter(
          (price) => price.schemaId === selectedSchema,
        );
      }
      setItemPrices(prices);
      setFilteredPrices(prices);
      setShowPrices(true);
    } catch (err) {
      setError('Failed to display item prices.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    setLoading(true);
    setError(null);
    try {
      // Use cached data instead of fetching again
      // Use filteredPrices to respect the item filter if active
      let prices = filteredPrices.length > 0 ? filteredPrices : allPriceData;
      if (selectedSchema !== 'ALL' && filteredPrices.length === 0) {
        prices = allPriceData.filter(
          (price) => price.schemaId === selectedSchema,
        );
      }
      if (!prices.length) {
        setError('No prices found for the selected schema.');
        setLoading(false);
        return;
      }
      const csvHeaders = ['schemaId', 'item', 'price'];
      const csvContent = [
        csvHeaders.join(','),
        ...prices.map((row) => [row.schemaId, row.item, row.price].join(',')),
      ].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'item_prices.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to download prices.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyToClipboard = async () => {
    const headers = ['schemaId', 'item', 'price'];
    const rows = filteredPrices.map((price) => [
      price.schemaId || '',
      price.item || '',
      price.price || '',
    ]);

    const ok = await copyToClipboard(buildTsv(headers, rows));
    if (!ok) {
      setError('Failed to copy to clipboard');
      return;
    }
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  // Generate template CSV for adding new item to all schemas
  const handleGenerateTemplate = () => {
    try {
      const itemName = itemFilter.trim();
      if (!itemName) {
        setError('Please enter an item name to generate a template.');
        return;
      }

      // Check which schemas are missing this item (case-insensitive comparison)
      const schemasWithItem = new Set(
        allPriceData
          .filter(
            (price) =>
              (price.item || '').toLowerCase() === itemName.toLowerCase(),
          )
          .map((p) => p.schemaId),
      );

      // Determine which schemas to include in template
      let targetSchemas = schemas;
      if (selectedSchema !== 'ALL') {
        targetSchemas = [selectedSchema];
      }

      const missingSchemasRows = targetSchemas
        .filter((schema) => !schemasWithItem.has(schema))
        .map((schema) => `${schema},${itemName},`); // Empty price for user to fill

      if (missingSchemasRows.length === 0) {
        setError(`"${itemName}" already exists in all selected schemas.`);
        return;
      }

      const csvHeaders = ['schemaId', 'item', 'price'];
      const csvContent = [csvHeaders.join(','), ...missingSchemasRows].join(
        '\n',
      );

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `template_${itemName}_missing_schemas.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to generate template:', err);
      setError('Failed to generate template CSV.');
    }
  };

  return (
    <main className={styles.container}>
      <h1>View Base Prices</h1>
      <nav className={styles.crossLinks} aria-label="Related pages">
        <Link to="/base-price-management" className={styles.crossLink}>
          Update Base Prices →
        </Link>
      </nav>
      <TipsPanel tips={BASE_PRICES_TIPS} />
      <section className={styles.card}>
        <h2>View/Download Base Prices</h2>
        <div className={styles.formRow}>
          <label htmlFor="schemaSearch">Select Schema:</label>
          <div className={styles.searchContainer}>
            <input
              id="schemaSearch"
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              placeholder="Type to search for a schema or select All Schemas"
              className={styles.searchInput}
              disabled={loading}
            />
            {showDropdown && (
              <div className={styles.dropdown}>
                <div
                  className={`${styles.dropdownItem} ${
                    selectedSchema === 'ALL' ? styles.selected : ''
                  }`}
                  onClick={() => handleSchemaSelect('ALL')}
                >
                  All Schemas
                </div>
                {filteredSchemas.map((schema) => (
                  <div
                    key={schema}
                    className={`${styles.dropdownItem} ${
                      selectedSchema === schema ? styles.selected : ''
                    }`}
                    onClick={() => handleSchemaSelect(schema)}
                  >
                    {schema}
                  </div>
                ))}
                {filteredSchemas.length === 0 &&
                  searchTerm &&
                  searchTerm !== 'All Schemas' && (
                    <div className={styles.noResults}>No schemas found</div>
                  )}
              </div>
            )}
          </div>
        </div>
        <div className={styles.buttonRow}>
          <button
            className={styles.button}
            onClick={handleViewPrices}
            disabled={loading || schemas.length === 0}
          >
            {loading ? 'Loading...' : 'View Item Prices'}
          </button>
          <button
            className={styles.button}
            onClick={handleDownload}
            disabled={loading || schemas.length === 0}
          >
            {loading ? 'Loading...' : 'Download as CSV'}
          </button>
        </div>
        {error && <div className={styles.errorMsg}>{error}</div>}
      </section>

      {showPrices && itemPrices.length > 0 && (
        <section className={styles.pricesCard}>
          <div className={styles.tableHeader}>
            <h2>
              Item Prices ({filteredPrices.length} of {itemPrices.length} items)
            </h2>
            <button
              className={styles.copyButton}
              onClick={handleCopyToClipboard}
              title="Copy table data to clipboard for pasting into Google Sheets"
            >
              {copySuccess ? '✓ Copied!' : '📋 Copy to Clipboard'}
            </button>
          </div>
          <div className={styles.filterRow}>
            <label htmlFor="itemFilter">Filter by Item:</label>
            <div className={styles.searchContainer}>
              <input
                id="itemFilter"
                type="text"
                value={itemFilterInput}
                onChange={handleItemFilterChange}
                onFocus={handleItemFilterFocus}
                onBlur={handleItemFilterBlur}
                placeholder="Type to search items..."
                className={styles.filterInput}
              />
              {showItemDropdown && (
                <>
                  {filteredItems.length > 0 && (
                    <div className={styles.dropdown}>
                      {filteredItems.map((item) => (
                        <div
                          key={item}
                          className={`${styles.dropdownItem} ${
                            itemFilterInput === item ? styles.selected : ''
                          }`}
                          onClick={() => handleItemSelect(item)}
                        >
                          {item}
                        </div>
                      ))}
                      {totalFilteredCount > 50 && (
                        <div className={styles.dropdownNote}>
                          Showing first 50 of {totalFilteredCount} matches. Keep
                          typing to narrow results.
                        </div>
                      )}
                    </div>
                  )}
                  {filteredItems.length === 0 && itemFilterInput.trim() !== '' && (
                    <div className={styles.dropdown}>
                      <div className={styles.noResults}>
                        No items found matching "{itemFilterInput}"
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
            {itemFilterInput && (
              <button
                className={styles.clearButton}
                onClick={() => {
                  clearItemFilterDebounce();
                  setItemFilter('');
                  setItemFilterInput('');
                  setShowItemDropdown(false);
                }}
                title="Clear filter"
              >
                ✕
              </button>
            )}
          </div>
          {itemCoverage && (
            <div className={styles.coverageInfo}>
              <div className={styles.coverageText}>
                <strong>"{itemCoverage.itemName}"</strong> found in{' '}
                <strong>
                  {itemCoverage.foundCount} of {itemCoverage.totalCount}
                </strong>{' '}
                schemas
                {itemCoverage.missingCount > 0 && (
                  <span className={styles.missingCount}>
                    {' '}
                    ({itemCoverage.missingCount} missing)
                  </span>
                )}
              </div>
              {itemCoverage.missingCount > 0 && (
                <button
                  className={styles.templateButton}
                  onClick={handleGenerateTemplate}
                  title={`Download CSV template to add "${itemCoverage.itemName}" to ${itemCoverage.missingCount} missing schemas`}
                >
                  📥 Download Template for {itemCoverage.missingCount} Missing
                  Schema{itemCoverage.missingCount !== 1 ? 's' : ''}
                </button>
              )}
            </div>
          )}
          <div className={styles.pricesTable}>
            <table>
              <thead>
                <tr>
                  <th>Schema ID</th>
                  <th>Item</th>
                  <th>Price</th>
                </tr>
              </thead>
              <tbody>
                {filteredPrices.length > 0 ? (
                  visiblePrices.map((price) => {
                    const rowKey = `${price.schemaId || 'schema'}-${
                      price.item || 'item'
                    }-${price.price ?? 'price'}`;
                    return (
                      <tr key={rowKey}>
                        <td>{price.schemaId || '-'}</td>
                        <td>{price.item || '-'}</td>
                        <td>${price.price || '-'}</td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="3" className={styles.emptyMessage}>
                      <div>No items match the filter "{itemFilter}"</div>
                      {itemFilter && showPrices && (
                        <div style={{ marginTop: '10px' }}>
                          <button
                            className={styles.templateButton}
                            onClick={handleGenerateTemplate}
                            title="Download a CSV template to add this item to missing schemas"
                          >
                            📥 Download Template to Add "{itemFilter}"
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                )}
                {filteredPrices.length > 0 &&
                  visibleCount < filteredPrices.length && (
                    <tr ref={sentinelRef}>
                      <td colSpan="3" className={styles.loadingRow}>
                        Loading more...
                      </td>
                    </tr>
                  )}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </main>
  );
};

export default ItemPricesLanding;
